import { Link } from "react-router-dom";
import { Mail, Youtube, BookOpen, Heart } from "lucide-react";
import { LOGO_URL, MINISTRY, NAV_LINKS } from "@/lib/brand";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="text-cream"
      style={{ backgroundColor: "var(--navy-900)" }}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4" data-testid="footer-logo-link">
              <div className="w-12 h-12 flex items-center justify-center">
                <img src={LOGO_URL} alt="" className="w-full h-full object-contain" />
              </div>
              <div className="font-display text-xl text-cream">Abide Daily</div>
            </Link>
            <p className="text-sm text-cream/80 leading-relaxed">
              {MINISTRY.tagline}. A Christ-centered place for daily Scripture, prayer, and encouragement.
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-display text-gold-light text-sm uppercase tracking-[0.18em] mb-4">
              Explore
            </h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.slice(0, 5).map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-cream/80 hover:text-gold-light transition-colors text-sm"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Ministry */}
          <div>
            <h4 className="font-display text-gold-light text-sm uppercase tracking-[0.18em] mb-4">
              Ministry
            </h4>
            <ul className="space-y-2.5">
              {NAV_LINKS.slice(5).map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-cream/80 hover:text-gold-light transition-colors text-sm"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Scripture & connect */}
          <div>
            <h4 className="font-display text-gold-light text-sm uppercase tracking-[0.18em] mb-4">
              A Word for Today
            </h4>
            <blockquote className="scripture text-cream/90 text-[0.95rem] leading-relaxed border-l-2 border-gold pl-4 mb-5">
              "Abide in me, and I in you. As the branch cannot bear fruit of itself, except it abide in the vine; no more can ye, except ye abide in me."
              <footer className="not-italic mt-2 text-gold-light text-xs tracking-wider">— John 15:4</footer>
            </blockquote>
            <div className="flex items-center gap-3">
              <a
                href={`mailto:${MINISTRY.email}`}
                data-testid="footer-email"
                aria-label="Email"
                className="w-10 h-10 rounded-full border border-cream/30 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy-deep transition-all"
              >
                <Mail size={18} />
              </a>
              <a
                href={MINISTRY.youtube}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-youtube"
                aria-label="YouTube"
                className="w-10 h-10 rounded-full border border-cream/30 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy-deep transition-all"
              >
                <Youtube size={18} />
              </a>
              <Link
                to="/bible-studies"
                aria-label="Bible Studies"
                className="w-10 h-10 rounded-full border border-cream/30 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy-deep transition-all"
              >
                <BookOpen size={18} />
              </Link>
              <Link
                to="/prayer-requests"
                aria-label="Prayer"
                className="w-10 h-10 rounded-full border border-cream/30 flex items-center justify-center hover:bg-gold hover:border-gold hover:text-navy-deep transition-all"
              >
                <Heart size={18} />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-cream/15 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-cream/60">
            © {new Date().getFullYear()} {MINISTRY.name}. To God be the glory.
          </p>
          <p className="text-xs text-cream/60 italic">
            "He must increase, but I must decrease." — John 3:30
          </p>
        </div>
      </div>
    </footer>
  );
}
